import React, { useEffect, useState } from 'react';
import { BurgerMenu } from 'components/BurgerMenu';
import { Grid } from '@material-ui/core';
import { HeaderParameters } from '../components';
import { useCompany, useCompanyFunctions } from 'contexts/CompanyContext';
import CompanyForm from 'components/CompanyForm';
import companyIcon from '_assets/parameters/online-store.svg';
import DefaultButton from 'components/DefaultButton';
import useStyles from './styles';

const initialCompanyState = {
  activity: '',
  address_name: '',
  category: '',
  city_name: '',
  kbisURL: '',
  social_name: '',
  siren: '',
  street_index: '',
  street_number: '',
  subcategory: '',
  zip_code: '',
};

const CompaniesParameters = () => {
  const classes = useStyles();

  const { companies: globalCompanies, currentCompany } = useCompany();
  const { createCompany, deleteCompany, editCompany, editCompanyDocument } = useCompanyFunctions();

  const [activeCompanyName, setActiveCompanyName] = useState(currentCompany?.social_name ?? '');
  const [companies, setCompanies] = useState(globalCompanies);
  const [isNewCompany, setNewCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(currentCompany ?? initialCompanyState);

  const [formDisabled, setFormDisabled] = useState(true);

  useEffect(() => {
    setCompanies(globalCompanies);
  }, [globalCompanies]);

  const onDocumentChange = async (document) => {
    let docUpdated = false;
    if (document?.file) {
      let formData = new FormData();
      formData.append('file', document?.file);
      docUpdated = await editCompanyDocument(currentCompany?.id, 'kbis', formData, document?.url);
    }

    return docUpdated;
  };

  const handleCreateCompany = async (values, document) => {
    const body = {
      social_name: values.social_name,
      siren: values.siren,
      activity: values.activity,
      address: values.address_name,
      index: values.street_index,
      street_number: values.street_number,
      postalCode: values.zip_code,
      city: values.city_name,
    };

    let documentData = null;
    if (document?.file) {
      let formData = new FormData();
      formData.append('file', document?.file);
      documentData = {
        data: formData,
        name: 'kbis',
        url: document?.url,
      };
    }

    const { created, documentUpdated, item } = await createCompany(values, body, documentData);
    if (created) {
      setActiveCompanyName(item.social_name);
      setFormDisabled(true);
      setNewCompany(false);
      setSelectedCompany(item);
    }

    return { documentUpdated, success: created };
  };

  const handleUpdateCompany = async (values) => {
    const body = {
      siren: values.siren,
      social_name: values.social_name,
      activity: values.activity,
      address_name: values.address_name,
      index: values.street_index,
      street_number: values.street_number,
      zip_code: values.zip_code,
      city: values.city_name,
    };

    const { edited, item } = await editCompany(values, body);
    if (edited) {
      setActiveCompanyName(values.social_name);
      setSelectedCompany(item);
      setFormDisabled(true);
    }

    return { success: edited };
  };

  const onCompanyClick = (index) => {
    if (isNewCompany) return;

    setFormDisabled(true);
    setSelectedCompany({ ...companies[index] });
    setActiveCompanyName(companies[index].social_name);
  };

  const onAddCompanyClick = () => {
    if (isNewCompany || !formDisabled) return;

    setCompanies((prev) => [...prev, { ...initialCompanyState, social_name: 'newCompany' }]);
    setSelectedCompany(initialCompanyState);
    setActiveCompanyName('newCompany');
    setNewCompany(true);
    setFormDisabled(false);
  };

  async function onRemoveCompanyClick() {
    deleteCompany(selectedCompany).then((deleted) => {
      if (deleted) {
        let index = companies.findIndex((c) => c.id === currentCompany.id);
        if (index === -1) index = 0;
        setSelectedCompany(companies[index]);
        setActiveCompanyName(companies[index].social_name);
      }
    });
  }

  const handleCancelForm = () => {
    if (isNewCompany) {
      setCompanies((old) => {
        old.splice(
          old.findIndex((c) => c.social_name === 'newCompany'),
          1,
        );
        return [...old];
      });
      let index = companies.findIndex((c) => c.id === currentCompany.id);
      if (index === -1) index = 0;

      setSelectedCompany(companies[index]);
      setActiveCompanyName(companies[index].social_name);
      setNewCompany(false);
    } else if (!formDisabled) {
      const index = companies.findIndex((c) => c.social_name === activeCompanyName);
      setSelectedCompany(companies[index]);
    }
    setFormDisabled(true);
  };

  const handleSubmitForm = async (values, document) => {
    if (isNewCompany) return await handleCreateCompany(values, document);
    else if (currentCompany !== null) return await handleUpdateCompany(values, document);
  };

  return (
    <Grid className={classes.root} container direction="column">
      <Grid className={classes.title} container item xs={12} justifyContent="center">
        <HeaderParameters image={companyIcon} noMargin text="Gestion entreprise" />
      </Grid>

      <Grid container item justifyContent="center" style={{ flexGrow: 0 }}>
        <Grid container item xs={12} md={9} alignItems="center" justifyContent="space-between">
          <Grid container item xs={11} justifyContent="center">
            {companies.map((company, index) => (
              <DefaultButton
                key={index}
                className={classes.buttonCompany}
                color={company.social_name === activeCompanyName ? 'primary' : 'default'}
                variant={company.social_name === activeCompanyName ? 'contained' : 'outlined'}
                standard={company.social_name === activeCompanyName}
                onClick={() => onCompanyClick(index)}
              >
                {company.social_name === 'newCompany'
                  ? selectedCompany.social_name === ''
                    ? 'Nouvelle entreprise'
                    : selectedCompany.social_name
                  : company.social_name}
              </DefaultButton>
            ))}
          </Grid>

          <Grid container item xs={1} justifyContent="flex-end">
            <BurgerMenu>
              <BurgerMenu.Item disabled={isNewCompany} onClick={onAddCompanyClick}>
                Ajouter
              </BurgerMenu.Item>
              <BurgerMenu.Item disabled={isNewCompany} onClick={() => setFormDisabled(false)}>
                Modifier
              </BurgerMenu.Item>
              <BurgerMenu.Item disabled={companies.length < 2 || isNewCompany} onClick={onRemoveCompanyClick}>
                Supprimer
              </BurgerMenu.Item>
            </BurgerMenu>
          </Grid>
        </Grid>
      </Grid>

      <Grid container item justifyContent="center" style={{ flexGrow: 1 }}>
        <CompanyForm
          autoSaveDocument={!isNewCompany}
          company={selectedCompany}
          disabled={formDisabled}
          enableDocument
          onCancel={handleCancelForm}
          onDocumentChange={onDocumentChange}
          onSubmit={handleSubmitForm}
        />
      </Grid>
    </Grid>
  );
};

export default CompaniesParameters;
